import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Eye, Download, Link as LinkIcon, Check, Sparkles, Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Featured surface on the Media Kit page: lets athletes preview / download /
 * share the AI-generated editorial media kit PDF. This is the PITCHD USP —
 * a 5-page premium PDF that becomes the athlete's commercial-identity asset.
 *
 * If the athlete uploaded a Canva kit, this still works (the upload wins for
 * outreach attachment, but the AI kit is always renderable on demand).
 */
export default function AIPdfPanel({ user }) {
  const [shareSlug, setShareSlug] = useState(null);
  const [copied, setCopied] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [lastRegenerated, setLastRegenerated] = useState(null);

  useEffect(() => {
    api.get("/media-kit/editorial").then(({ data }) => {
      setShareSlug(data?.share_slug || null);
    }).catch(() => {});
  }, []);

  const preview = () => {
    setPreviewing(true);
    // Open in a new tab. Backend serves with Content-Disposition: inline.
    window.open(`${BACKEND}/api/media-kit/ai-pdf?ts=${Date.now()}`, "_blank", "noopener");
    setTimeout(() => setPreviewing(false), 1500);
  };
  const download = () => {
    // Use a synthetic <a> click so we get the right filename + saving behaviour
    const url = `${BACKEND}/api/media-kit/ai-pdf?download=true&ts=${Date.now()}`;
    const a = document.createElement("a"); a.href = url; a.rel = "noopener";
    document.body.appendChild(a); a.click(); a.remove();
  };
  const copyLink = async () => {
    if (!shareSlug) return toast.error("Save your media kit first to generate a share link.");
    const url = `${window.location.origin.replace(/\/$/, "")}/kit/${shareSlug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
      toast.success("Public link copied — share it anywhere.");
    } catch { toast.error("Couldn't copy — paste manually: " + url); }
  };
  const regenerate = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const { data } = await api.post("/ai/media-kit/regenerate-editorial");
      setLastRegenerated(new Date().toLocaleTimeString());
      const filled = (data.career_highlights?.length || 0) + (data.honours?.length || 0) +
                     (data.pull_quote ? 1 : 0);
      toast.success(`Editorial refreshed — ${filled} new fields filled by AI.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Regenerate failed");
    } finally { setRegenerating(false); }
  };

  return (
    <section className="surface mb-8 overflow-hidden" data-testid="ai-pdf-panel">
      <div className="relative p-6 md:p-8">
        {/* Volt corner accent */}
        <div className="absolute top-0 left-0 w-1 h-12 bg-volt" />
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="min-w-0 flex-1">
            <div className="label-eyebrow flex items-center gap-2"><Sparkles className="w-3 h-3 text-volt" /> AI media kit · PITCHD</div>
            <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tighter mt-2" data-testid="ai-pdf-title">Your commercial identity</h2>
            <p className="text-ink-muted text-sm mt-3 max-w-xl">
              A 5-page editorial PDF built from your live profile data — cover, story, audience, brand fit, partnership CTA. Refreshes automatically as your stats grow. Brands receive it attached to every outreach email.
            </p>
            <div className="flex flex-wrap gap-2 mt-4 text-[10px] uppercase tracking-widest text-ink-muted">
              <span className="border border-volt/40 text-volt px-2 py-1">Live data</span>
              <span className="border border-line px-2 py-1">QR · live profile</span>
              <span className="border border-line px-2 py-1">Verified badge</span>
              <span className="border border-line px-2 py-1">Editorial layout</span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-6">
          <button onClick={preview} data-testid="ai-pdf-preview-btn"
            className="btn-volt text-sm inline-flex items-center gap-2 disabled:opacity-40" disabled={previewing}>
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            Preview AI media kit
          </button>
          <button onClick={download} data-testid="ai-pdf-download-btn"
            className="btn-ghost-line text-sm inline-flex items-center gap-2">
            <Download className="w-4 h-4" /> Download PDF
          </button>
          <button onClick={regenerate} data-testid="ai-pdf-regenerate-btn"
            className="btn-ghost-line text-sm inline-flex items-center gap-2 disabled:opacity-40"
            disabled={regenerating}
            title="Regenerate the editorial fields (pull quote, career highlights, audience demographics) using AI">
            {regenerating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {regenerating ? "Regenerating…" : "Regenerate with AI"}
          </button>
          <button onClick={copyLink} data-testid="ai-pdf-copy-link-btn"
            className="btn-ghost-line text-sm inline-flex items-center gap-2 disabled:opacity-40" disabled={!shareSlug}>
            {copied ? <Check className="w-4 h-4 text-volt" /> : <LinkIcon className="w-4 h-4" />}
            {copied ? "Copied" : "Copy public link"}
          </button>
        </div>
        {lastRegenerated && (
          <div className="mt-3 flex items-center gap-2 text-[11px] text-ink-muted" data-testid="ai-pdf-regen-meta">
            <Sparkles className="w-3 h-3 text-volt" /> Editorial refreshed at {lastRegenerated} · open Preview to see the new layout.
          </div>
        )}
        {!shareSlug && (
          <div className="mt-4 flex items-center gap-2 text-[11px] text-ink-muted">
            <AlertCircle className="w-3 h-3" /> The public link unlocks once you save your media kit once.
          </div>
        )}
      </div>
    </section>
  );
}
